import { createTheme } from '@mui/material/styles';
import { darkColors, lightColors, TRANSITIONS, ZINDEX, TOUCH } from '@/core/platform/platformStandards';

// Function to add custom typography variants to a theme
const addCustomTypography = (theme, colors) => {
  theme.typography.financialLarge = {
    fontSize: '1.5rem',
    fontWeight: 600,
    fontFamily: 'JetBrains Mono, monospace',
    fontFeatureSettings: '"tnum"',
    color: colors.text.primary,
  };

  theme.typography.financialRegular = {
    fontSize: '0.875rem',
    fontWeight: 500,
    fontFamily: 'JetBrains Mono, monospace',
    fontFeatureSettings: '"tnum"',
    color: colors.text.primary,
  };

  theme.typography.financialSmall = {
    fontSize: '0.8125rem',
    fontWeight: 500,
    fontFamily: 'JetBrains Mono, monospace',
    fontFeatureSettings: '"tnum"',
    color: colors.text.secondary,
  };

  theme.typography.chip = {
    fontSize: '0.75rem',
    fontWeight: 500,
    letterSpacing: '0.3px',
    color: colors.text.primary,
  };

  theme.typography.tableHeader = {
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: colors.text.secondary,
  };
  
  return theme;
};

// Create theme function
const createAppTheme = (mode = 'light') => {
  const colors = mode === 'dark' ? darkColors : lightColors;
  
  const theme = createTheme({
    spacing: 8,  // Keep Material-UI default
    
  palette: {
      mode: mode,
      
    primary: {
        main: colors.actions.primary,
        light: mode === 'dark' ? '#22d3ee' : '#4fd1c7',
        dark: colors.actions.primaryHover,
        contrastText: mode === 'dark' ? '#0f172a' : '#ffffff',
      },
      
    secondary: {
        main: colors.actions.secondary,
        light: mode === 'dark' ? '#64748b' : '#f7fafc',
        dark: mode === 'dark' ? '#334155' : '#cbd5e0',
        contrastText: colors.text.primary,
      },
      
      error: {
        main: colors.status.error,
        light: mode === 'dark' ? '#fca5a5' : '#fc8181',
        dark: mode === 'dark' ? '#dc2626' : '#c53030',
      },
      
      warning: {
        main: colors.status.warning,
        light: mode === 'dark' ? '#fcd34d' : '#f6e05e',
        dark: mode === 'dark' ? '#d97706' : '#b7791f',
      },
      
      info: {
        main: colors.status.info,
        light: mode === 'dark' ? '#60a5fa' : '#63b3ed',
        dark: mode === 'dark' ? '#2563eb' : '#2c5282',
      },
      
      success: {
        main: colors.status.success,
        light: mode === 'dark' ? '#4ade80' : '#68d391',
        dark: mode === 'dark' ? '#16a34a' : '#2f855a',
      },
      
    background: {
        default: colors.background.main,
        paper: colors.background.paper,
      },
      
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
        disabled: colors.text.muted,
      },

      /**
       * Default hairline for rules, OutlinedInput, Divider, etc.
       * Light/dark values come from platformStandards `borders.subtle`.
       * In components prefer: `sx={{ borderColor: 'divider' }}` with `border: 1, borderStyle: 'solid'`
       * or `theme.palette.custom.border.hairline` when you need a full border string.
       */
      divider: colors.borders.subtle,
      
      action: {
        hover: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        selected: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        disabled: mode === 'dark' ? 'rgba(255, 255, 255, 0.26)' : 'rgba(0, 0, 0, 0.26)',
        disabledBackground: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
      },
      
      // Custom color additions for specific use cases
      custom: {
        navigation: colors.navigation,
        borders: colors.borders,
        /** Mode-correct full border strings (use in sx or styled components). */
        border: {
          hairline: `1px solid ${colors.borders.subtle}`,
          emphasis: `1px solid ${colors.borders.primary}`,
          focus: `1px solid ${colors.borders.focus}`,
        },
        charts: colors.charts,
        roles: colors.roles,
        forms: colors.forms,
        actions: colors.actions,
        text: colors.text, // Include text colors for consistent access
        background: colors.background, // Include background colors for consistent access
        status: colors.status, // Include status colors for consistent access
        opportunities: colors.opportunities, // Custom opportunity colors for pie charts
        highlighter: colors.highlighter, // Active-state accent (genie icon, active indicators)
      },
    },
    
  typography: {
    fontFamily: [
        'Inter',
      'Roboto',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Arial',
      'sans-serif',
    ].join(','),
      
      // Headers
      h1: {
        fontSize: '2rem',        // 32px
        fontWeight: 600,
        lineHeight: 1.2,
        margin: '0 0 12px 0',
        color: colors.text.primary,
      },
      h2: {
        fontSize: '1.5rem',      // 24px
        fontWeight: 600,
        lineHeight: 1.3,
        margin: '0 0 12px 0',
        color: colors.text.primary,
      },
      h3: {
        fontSize: '1.25rem',     // 20px
        fontWeight: 600,
        lineHeight: 1.4,
        margin: '0 0 9px 0',
        color: colors.text.primary,
      },
      h4: {
        fontSize: '1.125rem',    // 18px
        fontWeight: 600,
        lineHeight: 1.4,
        margin: '0 0 9px 0',
        color: colors.text.primary,
      },
      h5: {
        fontSize: '1rem',        // 16px
        fontWeight: 600,
        lineHeight: 1.4,
        margin: '0 0 6px 0',
        color: colors.text.primary,
      },
      h6: {
        fontSize: '0.875rem',    // 14px
        fontWeight: 600,
        lineHeight: 1.4,
        margin: '0 0 6px 0',
        color: colors.text.primary,
      },

      // Body Text
      body1: {
        fontSize: '1rem',        // 16px
        fontWeight: 400,
        lineHeight: 1.6,
        margin: '0 0 6px 0',
        color: colors.text.primary,
      },
      body2: {
        fontSize: '0.875rem',    // 14px
        fontWeight: 400,
        lineHeight: 1.5,
        margin: '0 0 6px 0',
        color: colors.text.secondary,
      },

      // UI Elements
      subtitle1: {
        fontSize: '1rem',        // 16px
        fontWeight: 500,
        lineHeight: 1.4,
        color: colors.text.primary,
      },
      subtitle2: {
        fontSize: '0.875rem',    // 14px
        fontWeight: 500,
        lineHeight: 1.4,
        color: colors.text.secondary,
      },
      caption: {
        fontSize: '0.75rem',     // 12px
        fontWeight: 400,
        lineHeight: 1.4,
        color: colors.text.muted,
      },
      overline: {
        fontSize: '0.75rem',     // 12px
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: colors.text.secondary,
      },

      // Button Text
      button: {
        fontSize: '0.875rem',    // 14px
        fontWeight: 500,
        letterSpacing: '0.5px',
        textTransform: 'none',
        color: colors.text.primary,
      },
      
      // Custom typography variants for financial app
      financialLarge: {
        fontSize: '1.5rem',       // 24px
        fontWeight: 600,
        fontFamily: 'JetBrains Mono, monospace',
        fontFeatureSettings: '"tnum"',
        color: colors.text.primary,
      },
      financialRegular: {
        fontSize: '0.875rem',     // 14px
        fontWeight: 500,
        fontFamily: 'JetBrains Mono, monospace',
        fontFeatureSettings: '"tnum"',
        color: colors.text.primary,
      },
      financialSmall: {
        fontSize: '0.8125rem',    // 13px
        fontWeight: 500,
        fontFamily: 'JetBrains Mono, monospace',
        fontFeatureSettings: '"tnum"',
        color: colors.text.secondary,
      },
      chip: {
        fontSize: '0.75rem',      // 12px
        fontWeight: 500,
        letterSpacing: '0.3px',
      },
      tableHeader: {
        fontSize: '0.75rem',      // 12px
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        color: colors.text.secondary,
      },
    },
    
  shape: {
    borderRadius: 8,
  },
    
  transitions: {
    duration: TRANSITIONS.duration,
    easing: TRANSITIONS.easing,
    create: (props = ['all'], options = {}) => {
      const {
        duration = TRANSITIONS.duration.standard,
        easing = TRANSITIONS.easing.easeInOut,
        delay = 0,
      } = options;
      return Array.isArray(props)
        ? props.map(prop => `${prop} ${duration}ms ${easing} ${delay}ms`).join(', ')
        : `${props} ${duration}ms ${easing} ${delay}ms`;
    },
  },
    
  components: {
      // AppBar customization for navigation
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.navigation.topBar,
            boxShadow: mode === 'dark' ? 'none' : '0 1px 3px rgba(0,0,0,0.12)',
            borderBottom: `1px solid ${colors.borders.subtle}`,
          },
        },
      },
      
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.navigation.sidebar,
            borderRight: `1px solid ${colors.borders.subtle}`,
          },
        },
      },
      
    MuiButton: {
      defaultProps: {
        // Make all contained buttons use primary color by default
        variant: undefined, // Don't override variant
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
            margin: '4px', // Your existing spacing
          },
          contained: {
            // Apply primary color as default for all contained buttons
            // Explicit color="error" or color="secondary" will still override this
            backgroundColor: colors.actions.primary,
            color: mode === 'dark' ? colors.background.main : '#ffffff',
            boxShadow: mode === 'light' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
            '&:hover': {
              backgroundColor: colors.actions.primaryHover,
              boxShadow: mode === 'light' ? '0 4px 8px rgba(0,0,0,0.15)' : 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            },
          },
          // Explicit color props still work - they override the default
          containedPrimary: {
            backgroundColor: colors.actions.primary,
            color: mode === 'dark' ? colors.background.main : '#ffffff',
            '&:hover': {
              backgroundColor: colors.actions.primaryHover,
            },
          },
          containedSecondary: {
            backgroundColor: colors.actions.secondary,
            color: colors.text.primary,
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#64748b' : '#cbd5e0',
            },
          },
          containedError: {
            backgroundColor: colors.status.error,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: mode === 'dark' ? '#dc2626' : '#c53030',
            },
          },
          outlined: {
            borderColor: colors.borders.primary,
            color: colors.text.primary,
            '&:hover': {
              borderColor: colors.borders.focus,
              backgroundColor: mode === 'light' ? 'rgba(56, 178, 172, 0.04)' : 'rgba(56, 178, 172, 0.08)',
            },
          },
          sizeSmall: {
            padding: '4px 8px',
            '@media (pointer: coarse)': {
              minHeight: TOUCH.minSize,
              padding: '8px 12px',
            },
          },
          sizeMedium: {
            padding: '6px 12px',
            '@media (pointer: coarse)': {
              minHeight: TOUCH.minSize,
            },
          },
          sizeLarge: {
            padding: '8px 16px',
            '@media (pointer: coarse)': {
              minHeight: TOUCH.minSize,
            },
          },
        },
      },
      
      // Container spacing without !important - YOUR EXACT SPACING
      MuiContainer: {
        styleOverrides: {
          root: {
            paddingLeft: '9px',
            paddingRight: '9px',
            paddingTop: '6px',
            paddingBottom: '6px',
            '@media (min-width: 600px)': {
              paddingLeft: '12px',
              paddingRight: '12px',
              paddingTop: '9px',
              paddingBottom: '9px',
            },
          },
        },
      },
      
      // Paper components - YOUR EXACT SPACING with color additions
      MuiPaper: {
        styleOverrides: {
          root: {
            // Universal padding for all Paper components (reduced by 50%) - YOUR SPACING
            padding: '8px', // 1 * spacing unit (8px)
            marginBottom: '8px', // Standard bottom margin (reduced)
            
            // Add investment platform colors
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.borders.subtle}`,
            boxShadow: mode === 'light' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
            
            // Special handling for full-width headers - YOUR EXACT LOGIC
            '& > *:first-child[class*="MuiBox-root"]': {
              // If first child is a Box with background, make it full-width
              '&[style*="background"], &[class*="background"]': {
                margin: '-8px -8px 8px -8px', // Negative margins to extend to edges, 8px bottom spacing
                padding: '8px', // Restore internal padding
                borderRadius: '8px 8px 0 0', // Match Paper's border radius
              },
            },
          },
        },
      },
      
      // Card components - YOUR EXACT SPACING
      MuiCard: {
        styleOverrides: {
          root: {
            marginBottom: '9px', // Your spacing
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.borders.subtle}`,
            boxShadow: mode === 'light' ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
          },
        },
      },
      
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '9px', // Your spacing
            '&:last-child': {
              paddingBottom: '9px', // Your spacing
            },
          },
        },
      },
      
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: '9px', // Your spacing
          },
          title: {
            color: colors.text.primary,
          },
          subheader: {
            color: colors.text.secondary,
          },
        },
      },
      
      MuiCardActions: {
        styleOverrides: {
          root: {
            padding: '6px 9px', // Your spacing
          },
        },
      },
      
      // List components - YOUR EXACT SPACING
      MuiList: {
        styleOverrides: {
          root: {
            paddingTop: '2px',
            paddingBottom: '2px',
            backgroundColor: 'transparent',
          },
        },
      },
      
      MuiListItem: {
        styleOverrides: {
          root: {
            paddingTop: '2px',
            paddingBottom: '2px',
            paddingLeft: '9px',
            paddingRight: '9px',
            '&:hover': {
              backgroundColor: colors.borders.subtle,
            },
          },
        },
      },
      
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: '30px', // Your reduced width
            color: colors.text.secondary,
          },
        },
      },
      
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: colors.text.primary,
          },
          secondary: {
            color: colors.text.secondary,
          },
        },
      },
      
      // Dialog components - YOUR EXACT SPACING
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '12px 16px', // Your spacing
            backgroundColor: colors.navigation.sidebar,
            color: colors.text.primary,
          },
        },
      },
      
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '9px 16px', // Your spacing
            backgroundColor: colors.background.paper,
          },
        },
      },
      
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '6px 16px', // Your spacing
            backgroundColor: colors.background.paper,
          },
        },
      },
      
      // Form components - YOUR EXACT SPACING
      MuiFormControl: {
        styleOverrides: {
          root: {
            margin: '6px 0', // Your spacing
          },
        },
      },
      
      MuiTextField: {
        styleOverrides: {
          root: {
            margin: '6px 0',
            '& .MuiOutlinedInput-root': {
              backgroundColor: colors.forms.inputBg,
              '& fieldset': {
                borderColor: colors.forms.inputBorder,
              },
              '&:hover fieldset': {
                borderColor: colors.borders.primary,
              },
              '&.Mui-focused fieldset': {
                borderColor: colors.forms.inputFocus,
              },
            },
            '& .MuiInputLabel-root': {
              color: colors.text.secondary,
            },
            '& .MuiOutlinedInput-input': {
              color: colors.text.primary,
              '&::placeholder': {
                color: colors.forms.placeholder,
              },
            },
          },
        },
      },
      
      MuiInputBase: {
        styleOverrides: {
          root: {
            padding: '6px 9px',
            color: colors.text.primary,
          },
          sizeSmall: {
            '@media (pointer: coarse)': {
              minHeight: TOUCH.minSize,
            },
          },
        },
      },
      
      // Select components - CRITICAL for dropdown visibility
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: colors.forms.inputBg,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.forms.inputBorder,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.borders.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: colors.forms.inputFocus,
            },
          },
          select: {
            color: colors.text.primary,
            backgroundColor: colors.forms.inputBg,
          },
          icon: {
            color: colors.text.secondary,
          },
        },
      },
      
      // MenuItem components - CRITICAL for dropdown item visibility
      MuiMenuItem: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background.paper,
            color: colors.text.primary,
            '@media (pointer: coarse)': {
              minHeight: TOUCH.minSize,
            },
            '&:hover': {
              backgroundColor: colors.actions.primary,
              color: mode === 'dark' ? colors.background.main : '#ffffff',
            },
            '&.Mui-selected': {
              backgroundColor: colors.actions.primary,
              color: mode === 'dark' ? colors.background.main : '#ffffff',
              '&:hover': {
                backgroundColor: colors.actions.primaryHover,
              },
            },
            '&.Mui-focusVisible': {
              backgroundColor: colors.borders.focus,
              color: mode === 'dark' ? colors.background.main : '#ffffff',
            },
          },
        },
      },
      
      // Menu/Popover components - for dropdown container
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.borders.subtle}`,
            boxShadow: mode === 'light' ? '0 4px 8px rgba(0,0,0,0.15)' : '0 4px 8px rgba(0,0,0,0.4)',
            borderRadius: 8,
          },
          list: {
            padding: '4px 0',
          },
        },
      },
      
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.borders.subtle}`,
            boxShadow: mode === 'light' ? '0 4px 8px rgba(0,0,0,0.15)' : '0 4px 8px rgba(0,0,0,0.4)',
          },
        },
      },
      
      // Tab components
      MuiTabs: {
        styleOverrides: {
          root: {
            minHeight: 40,
            backgroundColor: colors.navigation.sidebar,
            '@media (pointer: coarse)': {
              minHeight: TOUCH.comfySize,
            },
          },
          indicator: {
            backgroundColor: colors.actions.primary,
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 40,
            padding: '6px 12px',
            color: colors.text.secondary,
            '@media (pointer: coarse)': {
              minHeight: TOUCH.comfySize,
              padding: '8px 16px',
            },
            '&.Mui-selected': {
              color: colors.actions.primary,
            },
          },
        },
      },
      
      // Table components - YOUR EXACT SPACING
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: colors.navigation.sidebar,
          },
        },
      },
      
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: '6px 9px', // Your spacing
            borderBottom: `1px solid ${colors.borders.subtle}`,
            color: colors.text.primary,
          },
          head: {
            padding: '9px', // Your spacing
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: colors.text.secondary,
            backgroundColor: colors.navigation.sidebar,
          },
        },
      },
      
      // Accordion components
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            padding: '0 9px',
            minHeight: 40,
            backgroundColor: colors.navigation.sidebar,
            '@media (pointer: coarse)': {
              minHeight: TOUCH.comfySize,
            },
          },
        },
      },
      
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            padding: '6px 9px 9px', // Your spacing
          },
        },
      },
      
      // Chip components - YOUR EXACT SPACING
      MuiChip: {
        styleOverrides: {
          root: {
            margin: '4px', // Your increased margin for better spacing
            fontWeight: 500,
            fontSize: '0.75rem',
            backgroundColor: colors.actions.secondary,
            color: colors.text.primary,
            border: `1px solid ${colors.borders.primary}`,
          },
          colorPrimary: {
            backgroundColor: colors.actions.primary,
            color: mode === 'dark' ? colors.background.main : '#ffffff',
            border: `1px solid ${colors.actions.primary}`,
          },
          colorSecondary: {
            backgroundColor: colors.actions.secondary,
            color: colors.text.primary,
            border: `1px solid ${colors.borders.primary}`,
          },
          // Only default-color filled chips use secondary fill; a blanket `filled` rule
          // was overriding `colorPrimary` / `colorSecondary` (primary showed mainly on hover).
          filled: {
            '&.MuiChip-colorDefault': {
              backgroundColor: colors.actions.secondary,
              color: colors.text.primary,
            },
          },
          outlined: {
            backgroundColor: 'transparent',
            color: colors.text.primary,
            border: `1px solid ${colors.borders.primary}`,
          },
          icon: {
            fontSize: TOUCH.iconSize,
          },
        },
      },
      
      // AppBar/Toolbar - YOUR EXACT SPACING
      MuiToolbar: {
        styleOverrides: {
          root: {
            paddingLeft: '9px', // Your spacing
            paddingRight: '9px', // Your spacing
            minHeight: '48px', // Your spacing
            backgroundColor: colors.navigation.topBar,
          },
        },
      },
      
      // Box component - YOUR EXACT LOGIC
      MuiBox: {
        styleOverrides: {
          root: {
            // Default gap for flex layouts (reduced by 50%) - YOUR SPACING
            '&[style*="display: flex"], &[style*="display:flex"]': {
              gap: '8px', // 1 * spacing unit for better readability
            },
          },
        },
      },
      
      // Stack component spacing - YOUR DEFAULTS
      MuiStack: {
        defaultProps: {
          spacing: 1, // Reduced spacing for all stacks
        },
      },
      
      // Grid component spacing - YOUR DEFAULTS
      MuiGrid: {
        defaultProps: {
          spacing: 1, // Reduced spacing (8px) for all grids
        },
      },

      // Alert components - ensure theme color compliance
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: 8, // Match theme border radius
          },
          filledSuccess: {
            backgroundColor: colors.status.success,
            color: mode === 'dark' ? colors.background.main : '#ffffff',
          },
          filledError: {
            backgroundColor: colors.status.error,
            color: mode === 'dark' ? colors.background.main : '#ffffff',
          },
          filledWarning: {
            backgroundColor: colors.status.warning,
            color: mode === 'dark' ? colors.background.main : '#ffffff',
          },
          filledInfo: {
            backgroundColor: colors.status.info,
            color: mode === 'dark' ? colors.background.main : '#ffffff',
          },
          outlinedSuccess: {
            borderColor: colors.status.success,
            color: colors.status.success,
            backgroundColor: mode === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(56, 161, 105, 0.1)',
          },
          outlinedError: {
            borderColor: colors.status.error,
            color: colors.status.error,
            backgroundColor: mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(229, 62, 62, 0.1)',
          },
          outlinedWarning: {
            borderColor: colors.status.warning,
            color: colors.status.warning,
            backgroundColor: mode === 'dark' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(214, 158, 46, 0.1)',
          },
          outlinedInfo: {
            borderColor: colors.status.info,
            color: colors.status.info,
            backgroundColor: mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(49, 130, 206, 0.1)',
          },
          standardSuccess: {
            color: colors.status.success,
            backgroundColor: mode === 'dark' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(56, 161, 105, 0.1)',
          },
          standardError: {
            color: colors.status.error,
            backgroundColor: mode === 'dark' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(229, 62, 62, 0.1)',
          },
          standardWarning: {
            color: colors.status.warning,
            backgroundColor: mode === 'dark' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(214, 158, 46, 0.1)',
          },
          standardInfo: {
            color: colors.status.info,
            backgroundColor: mode === 'dark' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(49, 130, 206, 0.1)',
          },
        },
      },

      // Svg icons — single standard from platform TOUCH.iconSize (tiles, Explore, chips, etc.)
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            fontSize: TOUCH.iconSize,
          },
        },
      },

      // Icon buttons — touch target enforcement on coarse-pointer devices
      MuiIconButton: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              minWidth: TOUCH.minSize,
              minHeight: TOUCH.minSize,
            },
          },
          sizeSmall: {
            '@media (pointer: coarse)': {
              minWidth: TOUCH.minSize,
              minHeight: TOUCH.minSize,
            },
          },
        },
      },

      // Checkbox — restore adequate padding on touch devices
      MuiCheckbox: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              padding: 8,
            },
          },
          sizeSmall: {
            '@media (pointer: coarse)': {
              padding: 8,
            },
          },
        },
      },

      // Radio — restore adequate padding on touch devices
      MuiRadio: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              padding: 8,
            },
          },
          sizeSmall: {
            '@media (pointer: coarse)': {
              padding: 8,
            },
          },
        },
      },

      // Slider — larger thumb on touch devices for accurate scrubbing
      MuiSlider: {
        styleOverrides: {
          thumb: {
            '@media (pointer: coarse)': {
              width: 24,
              height: 24,
            },
          },
        },
      },

      // Autocomplete — dropdown option height for touch
      MuiAutocomplete: {
        styleOverrides: {
          option: {
            '@media (pointer: coarse)': {
              minHeight: TOUCH.minSize,
            },
          },
        },
      },

      // ToggleButton — touch target enforcement
      MuiToggleButton: {
        styleOverrides: {
          root: {
            '@media (pointer: coarse)': {
              minHeight: TOUCH.minSize,
            },
          },
          sizeSmall: {
            '@media (pointer: coarse)': {
              minHeight: TOUCH.minSize,
            },
          },
        },
      },

      // Snackbar components - positioning and theme compliance
      MuiSnackbar: {
        styleOverrides: {
          root: {
            zIndex: ZINDEX.snackbar,
          },
        },
      },
    },
  });
  
  // Add custom typography with proper colors
  return addCustomTypography(theme, colors);
};

// Create default light theme
const theme = createAppTheme('light');

export default theme;
export { createAppTheme };